import type { ProviderRevenueResponse, SymbolRevenueResponse } from '@thinkalpha/locates-ws-client/contracts/orders.js';
import { log } from 'src/admin/LAB/log';
import type { AggregatedSymbolsRawRow, RevenueAnalyticsRawRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { lapi as axios } from 'src/api';
import { escapeODataValue } from 'src/lib/oData/escapeODataValue';

/** Options shared by both revenue analytics groupings. */
export interface RevenueAnalyticsQueryOptions {
    /**
     * Firm scope, sent as a `firmId eq …` `$filter` clause and-ed with the
     * others. Omitted loads the aggregate across every firm the caller can
     * see (the superuser all-firms view).
     */
    firmId?: string;
    /**
     * Additional OData `$filter` predicate over the numeric row fields
     * (`quantity`, `averagePrice`, `totalCost`, `currentPrice`), and-ed with
     * the other clauses, e.g. `(quantity gt 100) and (totalCost le 5000)`.
     */
    filter?: string;
    /** Zero-based starting row, sent as OData `$skip`. Omitted (with `top`) when searching a symbol. */
    skip?: number;
    /** Page size, sent as OData `$top`. Omitted (with `skip`) when searching a symbol. */
    top?: number;
    /**
     * OData `$orderby` expression over the row shape (`symbol`, `quantity`,
     * `averagePrice`, `totalCost`, `currentPrice`; the provider rows also
     * sort by `providerId`), e.g. `totalCost desc`. Omitted uses the
     * backend default sort (cost descending).
     */
    orderby?: string;
    /**
     * Exact-match symbol filter, sent as a `symbol eq …` `$filter` clause
     * and-ed with the others. Callers omit paging with it: a symbol yields
     * at most one row per group.
     */
    symbol?: string;
}

export interface GetRevenueAnalyticsOptions extends RevenueAnalyticsQueryOptions {
    /**
     * Limit the rows to these providers. Omitted (or empty) loads the
     * aggregate across every provider the caller can see.
     */
    providerIds?: ReadonlyArray<string>;
}

export type GetAggregatedSymbolsOptions = RevenueAnalyticsQueryOptions;

/** And the given clauses into one `$filter` expression, or undefined when none applies. */
const combineFilterClauses = (clauses: ReadonlyArray<string | undefined>): string | undefined => {
    const present = clauses.filter((clause): clause is string => Boolean(clause));
    if (present.length === 0) return undefined;
    if (present.length === 1) return present[0];
    return present.map((clause) => `(${clause})`).join(' and ');
};

const firmClause = (firmId: string | undefined): string | undefined =>
    firmId ? `firmId eq '${escapeODataValue(firmId)}'` : undefined;

const symbolClause = (symbol: string | undefined): string | undefined =>
    symbol ? `symbol eq '${escapeODataValue(symbol)}'` : undefined;

/**
 * Fetch one page of provider revenue analytics (one row per provider-symbol
 * position) and map the response onto the table's raw row shape. Each row
 * keeps its `providerId`; the display name is resolved later against the
 * loaded providers list, since the aggregate view spans providers.
 *
 * Served by `GET /v1/orders/revenue/symbols/providers`, driven entirely by
 * OData over the row shape: the firm scope (`firmId eq …`), the provider
 * selection (`providerId eq …`, or-ed across several providers), the symbol
 * search (`symbol eq …`), and the column `filter` are and-ed into one
 * `$filter`; sorting is `$orderby`, and paging `$skip`/`$top`. `inventory` is
 * optional per row, so the SOD figures are left `null` when the backend
 * has no start-of-day data (the table renders those as a dash).
 */
export const getRevenueAnalytics = async (
    options: GetRevenueAnalyticsOptions = {},
): Promise<RevenueAnalyticsRawRow[]> => {
    const { providerIds = [], firmId, filter, skip, top, orderby, symbol } = options;
    const providerClause =
        providerIds.length === 0
            ? undefined
            : providerIds.map((id) => `providerId eq '${escapeODataValue(id)}'`).join(' or ');
    if (TRACE) {
        log.trace(`Revenue analytics request: ${providerIds.length} provider(s), symbol ${symbol ? 'set' : 'unset'}`, {
            providerCount: providerIds.length,
            hasFirmScope: Boolean(firmId),
            hasSymbol: Boolean(symbol),
            hasColumnFilter: Boolean(filter),
            skip,
            top,
        });
    }
    const { data } = await axios.get<ProviderRevenueResponse>('/v1/orders/revenue/symbols/providers', {
        params: {
            $filter: combineFilterClauses([firmClause(firmId), providerClause, symbolClause(symbol), filter]),
            $orderby: orderby,
            $skip: skip,
            $top: top,
        },
    });
    return data.rows.map((row) => ({
        providerId: row.providerId,
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
        sodInventory: row.inventory?.startOfDay ?? null,
        remainingInventory: row.inventory?.remaining ?? null,
    }));
};

/**
 * Fetch one page of symbol revenue analytics — the provider rows collapsed
 * into one row per symbol — and map the response onto the Aggregated
 * Symbols tab's row shape.
 *
 * Served by `GET /v1/orders/revenue/symbols`, driven by the same OData
 * surface as {@link getRevenueAnalytics}: the firm scope (`firmId eq …`),
 * the symbol search (`symbol eq …`), and the column `filter` are and-ed
 * into one `$filter`; sorting is `$orderby`, and paging `$skip`/`$top`.
 */
export const getAggregatedSymbols = async (
    options: GetAggregatedSymbolsOptions = {},
): Promise<AggregatedSymbolsRawRow[]> => {
    const { firmId, filter, skip, top, orderby, symbol } = options;
    if (TRACE) {
        log.trace(`Aggregated symbols request: symbol ${symbol ? 'set' : 'unset'}`, {
            hasFirmScope: Boolean(firmId),
            hasSymbol: Boolean(symbol),
            hasColumnFilter: Boolean(filter),
            skip,
            top,
        });
    }
    const { data } = await axios.get<SymbolRevenueResponse>('/v1/orders/revenue/symbols', {
        params: {
            $filter: combineFilterClauses([firmClause(firmId), symbolClause(symbol), filter]),
            $orderby: orderby,
            $skip: skip,
            $top: top,
        },
    });
    return data.rows.map((row) => ({
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
    }));
};
